// The application shell: one canvas, one HUD, and the input that joins them.
//
// The §15 split is a file boundary here as much as a rendering one. This
// component owns the WebGL canvas element and the pointer, and it owns nothing
// else: it does not decode a frame, does not decide an outcome, and does not
// hold game state. What it does is the part that genuinely needs both halves —
// turning a pointer position into a board pixel, and a board pixel into a click.
//
// Everything it renders over the canvas is Tamagui (ui/), and everything it
// renders on the canvas is MapRenderer (render/). Nothing crosses.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TamaguiProvider } from 'tamagui';
import { RefuseOffBoard } from './net/clicks';
import type { Connection } from './net/connection';
import { ClickAttack, ClickBeachhead, ClickFortify, BoardW, NumPixels, type ClickKind } from './wire/core';
import { MapRenderer } from './render/mapRenderer';
import type { ArcField } from './render/arcs';
import type { Palette } from './render/palette';
import type { AudioEngine } from './audio/engine';
import type { Bus } from './audio/mixer';
import { config } from './ui/config';
import { Hud } from './ui/Hud';
import { reticleBox, type ReticleBox } from './ui/Reticle';
import type { HudStore } from './ui/hudState';
import { motionFlags, watchReducedMotion, writeOverride, type MotionStore } from './ui/motion';
import { VignetteClass } from './ui/css';

/**
 * A mutable slot rather than a callback.
 *
 * main.tsx drives the render loop and needs the renderer the moment the canvas
 * exists; this component creates it. A slot is the smallest thing that expresses
 * that without either side owning the other's lifecycle, and it is one field
 * instead of a subscription.
 */
export interface RendererSlot {
  current: MapRenderer | null;
}

export interface AppProps {
  connection: Connection;
  store: HudStore;
  audio: AudioEngine;
  arcs: ArcField;
  palette: Palette;
  rendererSlot: RendererSlot;
  motionStore?: MotionStore | null;
  /** Seeded from `prefers-reduced-motion` before the first paint. */
  initialReducedMotion: boolean;
}

/** How far a pointer may travel between down and up and still count as a click. */
const ClickSlopCss = 4;

export function App(props: AppProps): React.ReactNode {
  const { connection, store, audio, arcs, rendererSlot, initialReducedMotion } = props;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rendererRef = useRef<MapRenderer | null>(null);
  const [reticle, setReticle] = useState<ReticleBox | null>(null);
  const [reduced, setReduced] = useState(initialReducedMotion);

  const drag = useRef<{ id: number; x: number; y: number; moved: number } | null>(null);

  const dpr = (): number => (typeof devicePixelRatio === 'number' ? devicePixelRatio : 1);

  /**
   * Pushes the viewport to the server.
   *
   * §3 subscribes to "visible chunks plus a one-chunk margin, and a
   * subscribe/unsubscribe diff on pan". Connection.setSubscriptions already
   * diffs, so calling it on every pointer move during a drag costs one pass over
   * a hundred chunks and sends nothing when nothing changed.
   */
  const syncSubscriptions = useCallback(() => {
    const r = rendererRef.current;
    if (!r) return;
    connection.setSubscriptions(r.camera.visibleChunks(1));
  }, [connection]);

  // --- the canvas ---------------------------------------------------------

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let renderer: MapRenderer;
    try {
      renderer = new MapRenderer(canvas, { palette: props.palette });
    } catch (e) {
      // WebGL2 is required for the map layer. The HUD stays up and says so,
      // rather than the page going blank on a machine with a blocked context.
      store.setReject(0, e instanceof Error ? e.message : 'webgl2 unavailable');
      return;
    }
    renderer.arcs = arcs;
    renderer.reducedMotion = reduced;
    rendererRef.current = renderer;
    rendererSlot.current = renderer;

    const resize = (): void => {
      const rect = canvas.getBoundingClientRect();
      renderer.resize(rect.width, rect.height, dpr());
      syncSubscriptions();
    };
    const rect = canvas.getBoundingClientRect();
    renderer.resize(rect.width, rect.height, dpr());
    renderer.camera.fit();
    syncSubscriptions();

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    return () => {
      ro.disconnect();
      rendererSlot.current = null;
      rendererRef.current = null;
      renderer.dispose();
    };
    // The renderer is created once for the life of the canvas. Re-creating it on
    // a prop change would drop the board texture and the GL programs with it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- reduced motion -----------------------------------------------------

  useEffect(() => {
    if (rendererRef.current) rendererRef.current.reducedMotion = reduced;
    store.setReducedMotion(reduced);
    // The stylesheet keys its scanline and flicker rules off this attribute, so
    // the switch works on a machine whose system preference says otherwise.
    if (typeof document !== 'undefined') {
      document.documentElement.dataset.motion = reduced ? 'reduced' : 'full';
    }
  }, [reduced, store]);

  useEffect(() => {
    // The system preference can change mid-session, and a season is four weeks.
    return watchReducedMotion((v) => setReduced(v));
  }, []);

  const onReducedMotion = useCallback(
    (v: boolean) => {
      setReduced(v);
      writeOverride(props.motionStore ?? null, v);
    },
    [props.motionStore],
  );

  const onVolume = useCallback(
    (bus: Bus, v: number) => {
      audio.setVolume(bus, v);
      store.setAudio(audio.live(), audio.volumes());
    },
    [audio, store],
  );

  // --- pointer ------------------------------------------------------------

  const hoverAt = useCallback(
    (clientX: number, clientY: number) => {
      const renderer = rendererRef.current;
      const canvas = canvasRef.current;
      if (!renderer || !canvas) return;
      const rect = canvas.getBoundingClientRect();
      const scale = dpr();
      const p = renderer.camera.pixelAt((clientX - rect.left) * scale, (clientY - rect.top) * scale);
      if (p === renderer.hoverPixel) return;
      renderer.hoverPixel = p;
      if (p < 0) {
        store.setHover(null);
        setReticle(null);
        return;
      }
      const board = connection.board;
      store.setHover({
        pixel: p,
        x: p % BoardW,
        y: (p / BoardW) | 0,
        owner: board.countryAt(p),
        hp: board.hpAt(p),
        flags: board.flagsAt(p),
      });
      setReticle(reticleBox(renderer.camera, p, BoardW, scale));
      // §16: the reticle lock is one of the synthesised UI sounds, and it is the
      // quietest one in the table because it fires on every cell the pointer
      // crosses.
      audio.ui('reticle');
    },
    [audio, connection, store],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const d = drag.current;
      if (d && d.id === e.pointerId) {
        const renderer = rendererRef.current;
        if (renderer) {
          const scale = dpr();
          renderer.camera.panBy((e.clientX - d.x) * scale, (e.clientY - d.y) * scale);
          d.moved += Math.abs(e.clientX - d.x) + Math.abs(e.clientY - d.y);
          d.x = e.clientX;
          d.y = e.clientY;
          syncSubscriptions();
          if (renderer.hoverPixel >= 0) {
            setReticle(reticleBox(renderer.camera, renderer.hoverPixel, BoardW, scale));
          }
        }
        return;
      }
      hoverAt(e.clientX, e.clientY);
    },
    [hoverAt, syncSubscriptions],
  );

  /**
   * Which kind of click this is.
   *
   * The server decides for real; this is the same advisory call the click ledger
   * makes, and getting it wrong costs a refused click rather than a wrong board.
   * Beachhead is legal only while the country holds zero pixels (§5), so the scan
   * is the honest test — it runs once per click, and a click is once per 30 s.
   */
  const chooseKind = useCallback(
    (target: number): ClickKind => {
      const country = connection.country();
      if (country === 0) return ClickAttack;
      const board = connection.board;
      if (board.countryAt(target) === country) return ClickFortify;
      if (!holdsAnyPixel(board.country, country)) return ClickBeachhead;
      return ClickAttack;
    },
    [connection],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const d = drag.current;
      drag.current = null;
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      if (!d || d.moved > ClickSlopCss) return;

      const renderer = rendererRef.current;
      const canvas = canvasRef.current;
      if (!renderer || !canvas) return;
      const rect = canvas.getBoundingClientRect();
      const scale = dpr();
      const target = renderer.camera.pixelAt((e.clientX - rect.left) * scale, (e.clientY - rect.top) * scale);
      if (target < 0) {
        // Off the board, and that is a very easy click to make: a square board
        // in a wide viewport leaves dead canvas either side at fit-zoom, so the
        // margins are a large target. Returning silently here left the defined
        // "Off the map." refusal unreachable and gave the player no feedback at
        // all on the miss — they cannot tell a miss from a broken game.
        store.setRefusal(RefuseOffBoard, Date.now());
        return;
      }

      // §16's one elegant part: the first gesture in this game is a click on the
      // map, which is also the core verb, so audio unlocks here and there is no
      // "enable sound" interstitial anywhere.
      const armed = audio.unlock();
      if (armed) store.setAudio(audio.live(), audio.volumes());

      const kind = chooseKind(target);
      const board = connection.board;
      const refusal = connection.clicks.refuse(
        target,
        kind,
        connection.country(),
        (p, c) => board.isAdjacentTo(p, c),
        (p) => p >= 0 && p < NumPixels,
      );
      if (refusal) {
        store.setRefusal(refusal, Date.now());
        audio.ui('refuse');
        return;
      }
      const click = connection.click(target, kind);
      if (!click) {
        // The socket is not live. A click that was never sent must not leave a
        // marker on the map claiming it was — and it must not be silent either,
        // so it is reported as exactly what it is. "Never reached the board;
        // refunded" is the §3 vocabulary for this and it is literally true: the
        // click never left the tab.
        store.setOutcome('dropped', null, Date.now());
        audio.ui('refuse', { weight: 0.2 });
        return;
      }
      audio.ui('click', { weight: kind === ClickBeachhead ? 0.7 : 0.25 });
    },
    [audio, chooseKind, connection, store],
  );

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    drag.current = { id: e.pointerId, x: e.clientX, y: e.clientY, moved: 0 };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, []);

  const onWheel = useCallback(
    (e: React.WheelEvent<HTMLCanvasElement>) => {
      const renderer = rendererRef.current;
      const canvas = canvasRef.current;
      if (!renderer || !canvas) return;
      const rect = canvas.getBoundingClientRect();
      const scale = dpr();
      // Exponential in the wheel delta so a trackpad's small deltas and a mouse's
      // large ones both feel like the same gesture.
      const factor = Math.exp(-e.deltaY * 0.0015);
      renderer.camera.zoomAt((e.clientX - rect.left) * scale, (e.clientY - rect.top) * scale, factor);
      syncSubscriptions();
      if (renderer.hoverPixel >= 0) {
        setReticle(reticleBox(renderer.camera, renderer.hoverPixel, BoardW, scale));
      }
    },
    [syncSubscriptions],
  );

  const onPointerLeave = useCallback(() => {
    const renderer = rendererRef.current;
    if (renderer) renderer.hoverPixel = -1;
    store.setHover(null);
    setReticle(null);
  }, [store]);

  const flags = useMemo(() => motionFlags(reduced), [reduced]);

  return (
    <TamaguiProvider config={config} defaultTheme="dark">
      <div
        className={flags.scanlines ? VignetteClass : undefined}
        style={{ position: 'relative', width: '100%', height: '100%' }}
      >
        <canvas
          ref={canvasRef}
          className="wwp-map"
          onPointerMove={onPointerMove}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerLeave}
          onWheel={onWheel}
        />
        <Hud store={store} reticle={reticle} onVolume={onVolume} onReducedMotion={onReducedMotion} />
      </div>
    </TamaguiProvider>
  );
}

/** Whether a country holds any pixel at all. The §5 beachhead test. */
function holdsAnyPixel(country: Uint16Array, id: number): boolean {
  for (let i = 0; i < country.length; i++) {
    if (country[i] === id) return true;
  }
  return false;
}
